import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CircleParking, Plus } from "lucide-react";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { ParkingCard } from "@/components/ParkingCard";
import { useAuth } from "@/lib/auth";
import { fetchMyParking, projectParkingToViewport } from "@/lib/parking";
import { useCategoryAccess } from "@/lib/categories";
import { MOCK_PARKING } from "@/lib/parking-mock-data";

export const Route = createFileRoute("/_authenticated/partner/parking/")({
  head: () => ({
    meta: [
      { title: "My parking places · Landoo" },
      { name: "description", content: "Manage your parking listings, slots and availability." },
    ],
  }),
  component: PartnerParkingPage,
});

function PartnerParkingPage() {
  const { user } = useAuth();
  const { can, loading: catLoading } = useCategoryAccess();
  const { data = [], isLoading } = useQuery({
    queryKey: ["parking", "mine", user?.id],
    queryFn: () => fetchMyParking(user!.id),
    enabled: !!user,
  });

  const listings = projectParkingToViewport(data);
  const hasAccess = can("parking");

  return (
    <DashboardShell title="Parking places" role="Partner" items={PARTNER_NAV}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <CircleParking className="h-4 w-4 text-parking" />
          {isLoading ? "Loading…" : `${listings.length} parking listing(s)`}
        </div>
        {hasAccess && (
          <Link
            to="/partner/parking/new"
            className="inline-flex items-center gap-1.5 rounded-full bg-parking px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
          >
            <Plus className="h-4 w-4" /> Add parking place
          </Link>
        )}
      </div>

      {!catLoading && !hasAccess && (
        <div className="mt-4">
          <PlaceholderCard
            title="Parking access not enabled"
            description="Your partner account is not subscribed to the Parking category yet. Request access from your subscription page and an admin will enable it."
          >
            <Link
              to="/partner/subscription"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
            >
              Manage category access
            </Link>
          </PlaceholderCard>
        </div>
      )}

      {listings.length > 0 ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {listings.map((p) => (
            <ParkingCard key={p.id} parking={p} />
          ))}
        </div>
      ) : (
        hasAccess && (
          <div className="mt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Example listings (demonstration data)
            </div>
            <div className="grid gap-3 opacity-70 sm:grid-cols-2 lg:grid-cols-3">
              {MOCK_PARKING.slice(0, 3).map((p) => (
                <ParkingCard key={p.id} parking={p} />
              ))}
            </div>
          </div>
        )
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <PlaceholderCard
          title="Slot availability"
          description="Live slot occupancy tracking will appear here once parking bookings go live."
        />
        <PlaceholderCard
          title="Parking bookings"
          description="Booking requests and monthly parking customers — coming soon."
        />
      </div>
    </DashboardShell>
  );
}
